import { Button, IconButton, Link, Skeleton, Stack, Typography } from '@mui/material';

import isEmpty from 'lodash/isEmpty';

import ChevronRightIcon from '@/components/Icons/ChevronRightIcon';
import ExternalLinkIcon from '@/components/Icons/ExternalLinkIcon';
import LockIcon from '@/components/Icons/LockIcon';
import useIsTrainingEnabled from '@/hooks/useIsTrainingEnabled';
import { links } from '@/localization';
import { BasetenPageEnum } from '@/routes';

import { MAX_ITEMS_TO_SHOW } from '../../Training/constants';
import Training from '../../Training_Legacy';
import { useTrainingJobs } from '../../Training_Legacy/hooks';
import { WorkspaceSections } from '../../types';
import { AppLink, EmptyState, Section, TrainingAnimationContainer, ViewAllItems } from '../styled';

function Trainings() {
  const { trainingJobs, loading } = useTrainingJobs(true);
  const isTrainingEnabled = useIsTrainingEnabled();

  function getContent() {
    if (loading) {
      return (
        <Stack pt={1}>
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="text" width="40%" />
        </Stack>
      );
    }

    if (isEmpty(trainingJobs)) {
      return (
        <EmptyState gap={8}>
          <Stack gap={3}>
            <Stack gap={1} maxWidth={600}>
              <Typography variant="subtitle1">Start training and fine-tuning on Baseten</Typography>
              <Typography variant="body1" color="text.muted">
                Run reproducible, scalable training jobs with full control over data, code, compute,
                and runtime settings.{' '}
                <Link href={links.basetenDocsTrainingOverview} target="_blank">
                  Learn more
                </Link>
              </Typography>
            </Stack>
            {isTrainingEnabled ? (
              <Button
                variant="outlined"
                href={links.basetenDocsTrainingOverview}
                endIcon={<ExternalLinkIcon />}
                target="_blank"
                sx={{ width: 'fit-content' }}
              >
                Start a Training job
              </Button>
            ) : (
              <Button
                variant="outlined"
                href="https://www.baseten.co/talk-to-us/request-access/"
                target="_blank"
                sx={{ width: 'fit-content' }}
                endIcon={<LockIcon />}
              >
                Request access
              </Button>
            )}
          </Stack>
          <TrainingAnimationContainer />
        </EmptyState>
      );
    }

    return (
      <Stack gap={4} mt={-3}>
        <Stack mx={-4}>
          <Training minimal />
        </Stack>
        {trainingJobs.length > MAX_ITEMS_TO_SHOW && (
          <AppLink
            page={BasetenPageEnum.Workspace}
            params={{ section: WorkspaceSections.Training }}
          >
            <ViewAllItems>View all training jobs</ViewAllItems>
          </AppLink>
        )}
      </Stack>
    );
  }

  return (
    <Section>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack gap={1}>
          <Typography variant="h3">Training jobs</Typography>
          {!isEmpty(trainingJobs) && (
            <Typography variant="body1" color="text.muted">
              Run reproducible, scalable training jobs with full control.
            </Typography>
          )}
        </Stack>
        <AppLink page={BasetenPageEnum.Workspace} params={{ section: WorkspaceSections.Training }}>
          <IconButton data-cy="list-item-arrow">
            <ChevronRightIcon />
          </IconButton>
        </AppLink>
      </Stack>
      {getContent()}
    </Section>
  );
}

export default Trainings;
